import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { Prepaid } from './Prepaid/Prepaid';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { ThemeId } from '@/consts/shop';
import {
  getAndSetCustomerInfo,
  useCustomerInfo,
} from '@/hooks/api/customer/useCustomerInfo';
import { usePaymentList } from '@/hooks/api/payment/usePaymentList';
import { useLoadCustomer } from '@/hooks/useLoadCustomer';
import { Campaign } from '@/types/api/customerApi';
import { GmoRedirectInfo } from '@/types/payment';
import { paymentStorage } from '@/utils/paymentStorage';
import { generatePrepaidName } from '@/utils/shop';
import { storeDispatch } from '@/utils/store';
// 下記importを消去しないように注意
import '@/utils/permissionHelper';

type PrepaidPageProps = {
  f7router: Router.Router;
  status?: string;
  gmoRedirectInfo?: GmoRedirectInfo;
};

export const PrepaidPage: FC<PrepaidPageProps> = ({
  f7router,
  status,
  gmoRedirectInfo,
}) => {
  const router = f7.views.main.router;
  const [refresh, setRefresh] = useState(false);
  const authInfo = store.state.authInfo;
  const themeId = useStore(store, 'getThemeId') as ThemeId;
  const couponList =
    (useStore(store, 'getCampaignsInfo') as Required<Campaign>[]) || [];

  const validCouponList = couponList.filter((coupon) => !coupon.status);
  const [currentStatus, setCurrentStatus] = useState(status);
  const customerInfo = useStore(store, 'customerInfo');
  const prepaidInfo = useStore(store, 'getFirstAdvancePaymentsInfo');
  const prepaidName = generatePrepaidName(customerInfo?.affiliate_prepaid_name);
  const customerId = paymentStorage.getItem('m_customer_id');
  const { updatePaymentList, isFetched, isFetching } = usePaymentList(false);

  const token =
    paymentStorage.getItem('token') === null
      ? undefined
      : paymentStorage.getItem('token');

  const { refetch } = useCustomerInfo(
    {
      m_customer_id: authInfo.m_customer_id,
    },
    !status,
  );

  const { isLoggedIn, resetIsLoggedIn } = useLoadCustomer(
    router,
    token === null ? undefined : token,
    Number(customerId),
    currentStatus,
    false,
  );

  const handleOnRefetch = async (cb: () => void) => {
    try {
      await refetch();
      cb();
    } catch (e) {
      console.error('Error while refetching: ', e);
    }
  };

  const afterGmoRedirect = useCallback(async () => {
    // GMOからのリダイレクトに必要なパラメータ
    const mCustomerId = paymentStorage.getItem('m_customer_id');
    const destinationStep = paymentStorage.getItem('destination_step');
    const destinationPage = paymentStorage.getItem('destination_page');
    const reserveAmount = paymentStorage.getItem('reserve_amount');
    const sourcePage = paymentStorage.getItem('source_page');
    if (currentStatus) {
      storeDispatch('setMenu', 'menu', 'prepaid');
    }
    paymentStorage.removeItem('destination_step');
    paymentStorage.removeItem('destination_page');

    // GMOステータスURL（/gmo_success, /charge_prepaid_success 等）をクリーンアップ
    const cleanupGmoStatusUrl = () => {
      window.history.replaceState({}, '', paths.prepaid);
      const rh = router.history;
      if (rh.length > 0) {
        rh[rh.length - 1] = paths.prepaid;
      }
    };

    const gmoRedirectInfo = {
      status: currentStatus,
      destinationStep,
      destinationPage,
      homeNextPage: paths.prepaid,
      openDialog: paths.reservings,
    };
    reserveAmount &&
      (await storeDispatch(
        'setReserveAmount',
        'reserveAmount',
        Number(reserveAmount) ?? 0,
      ));

    switch (currentStatus) {
      // GMOペイメント post →　maikuru バックエンド（api.mobicon.net）からのリダイレクト
      case 'gmo_success':
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          resetIsLoggedIn();
          cleanupGmoStatusUrl();
          if (sourcePage === paths.chargePrepaid) {
            paymentStorage.removeItem('source_page');
            router.navigate(paths.chargePrepaid, {
              animate: false,
              props: {
                redirectPage: 'prepaid',
              },
            });
            return;
          }

          if (!isFetched && !isFetching) {
            await updatePaymentList(Number(mCustomerId));
            if (gmoRedirectInfo.destinationPage) {
              f7.views.main.router.navigate(gmoRedirectInfo.destinationPage, {
                animate: true,
                props: {
                  isBackShowPrepaidSummary: true,
                },
              });
            }
            return;
          }
        }
        break;
      case 'gmo_cancel':
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          resetIsLoggedIn();
          cleanupGmoStatusUrl();
          if (sourcePage === paths.chargePrepaid) {
            paymentStorage.removeItem('source_page');
            router.navigate(paths.chargePrepaid, {
              animate: false,
              props: {
                redirectPage: 'prepaid',
              },
            });
            return;
          }
          if (gmoRedirectInfo.destinationPage) {
            f7.views.main.router.navigate(gmoRedirectInfo.destinationPage, {
              animate: true,
              props: {
                isBackShowPrepaidSummary: true,
              },
            });
          }
        }
        break;
      case 'charge_prepaid_success':
        // チャージ処理はWebhook側で完了しているため、
        // フロントエンドでは顧客情報（残高含む）を再取得するだけ
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          paymentStorage.removeItem('charge_amount');
          paymentStorage.removeItem('postGmoNav');
          cleanupGmoStatusUrl();
          getAndSetCustomerInfo(
            {
              m_customer_id: authInfo.m_customer_id,
            },
            undefined,
            { applySelectedTenant: false },
          );
        }
        break;
      case 'charge_prepaid_cancel':
        // チャージキャンセル: 顧客情報を再取得しておサイフ画面を表示する
        if (isLoggedIn) {
          setCurrentStatus(undefined);
          paymentStorage.removeItem('charge_amount');
          paymentStorage.removeItem('postGmoNav');
          cleanupGmoStatusUrl();
          getAndSetCustomerInfo(
            {
              m_customer_id: authInfo.m_customer_id,
            },
            undefined,
            { applySelectedTenant: false },
          );
        }
        break;
      default:
        break;
    }
  }, [
    prepaidInfo,
    authInfo.m_customer_id,
    currentStatus,
    isFetched,
    isFetching,
    isLoggedIn,
    resetIsLoggedIn,
    router,
    updatePaymentList,
  ]);

  useEffect(() => {
    afterGmoRedirect();
  }, [afterGmoRedirect]);

  return (
    <Prepaid
      f7router={f7.view.main.router}
      themeId={themeId}
      couponList={couponList}
      prepaidName={prepaidName}
      customerInfo={customerInfo}
      validCouponList={validCouponList}
      prepaidInfo={prepaidInfo}
      onRefetch={handleOnRefetch}
    />
  );
};
